import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getCurrentPage,
  getSize,
  setTableCurrentPage,
  setTablePagination,
} from 'redux/slices/tableConfig';

import { budgetDataService } from 'services/budget.service';

import { useFetch } from 'hooks';

dayjs.extend(customParseFormat);

// Budgets change outside this page (sent / reopened from the catalogue), so the
// list refreshes itself while it is on screen
const LIVE_REFRESH_INTERVAL_MS = 30_000;
// focus and visibilitychange fire together when the tab comes back
const LIVE_REFRESH_MIN_GAP_MS = 2_000;

interface Budget {
  id: string;
  name: string;
  season: string;
  marketingLine: string;
  type: string;
  action: string[];
  startDate: string;
  endDate: string;
  num?: number;
  zone?: string;
  /** 'To Send' or 'Sent' (sent from the catalogue) */
  budgetStatus?: string;
}

const useBudget = () => {
  const dispatch = useAppDispatch();
  const page = useAppSelector(getCurrentPage);
  const size = useAppSelector(getSize);

  const [budgets, setBudgets] = useState<Budget[]>([]);

  const { data: budgetData, refetch } = useFetch(
    () => budgetDataService.getAll(page, size),
    [page, size]
  );

  // refetch is a new function on every render; the listeners below read the latest one
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    let lastRefreshAt = Date.now();

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt < LIVE_REFRESH_MIN_GAP_MS) return;

      lastRefreshAt = Date.now();
      refetchRef.current();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const timer = window.setInterval(refresh, LIVE_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.clearInterval(timer);
    };
  }, []);

  const INPUT_FORMAT = 'DD/MM/YYYY HH:mm:ss';

  useEffect(() => {
    if (budgetData) {
      const { totalElements, empty } = budgetData;

      if (totalElements > 0 && empty) {
        // If current page is out of range after server-side changes, go back to first page
        dispatch(setTableCurrentPage(0));
        return;
      }

      if (budgetData.content) {
        const budgetContent = budgetData.content;
        const mappedBudgets = budgetContent.map((budget: Budget) => ({
          ...budget,
          startDate: budget.startDate
            ? dayjs(budget.startDate, INPUT_FORMAT).format('YYYY-MM-DD')
            : '',
          endDate: budget.endDate
            ? dayjs(budget.endDate, INPUT_FORMAT).format('YYYY-MM-DD')
            : '',
          action: ['edit', 'delete'],
        }));
        setBudgets(mappedBudgets);
      }

      dispatch(setTablePagination({ totalPages: budgetData.totalPages }));
    }
  }, [budgetData, dispatch]);

  return {
    budgets,
    refetchBudgets: refetch,
  };
};

export { useBudget };
